import {
  BlurredFace,
  Floor,
  MatchingPoint,
  NavigationHotspot,
  PointImage,
  ProjectFloorPoint,
  ProjectInfo,
} from '../../../models/project.model';
import { Project360Service } from '../../p-dashboard/project360.service';
import { GStatic } from '../../../services/GStatic';
import { ObservationRefactor } from '../../../models/observations.model';

export class CurrentPImg implements PointImage {
  private _project: Project360Service;
  floor_id: string;
  id: string;
  image_360: string;
  image_360_thumb: string;
  navigation_hotspots: NavigationHotspot[];
  numbers_of_completed_observation: number;
  numbers_of_in_progress_observation: number;
  numbers_of_pending_observation: number;
  point_id: string;
  point_name: string;
  blurred_faces: BlurredFace[];
  height?: number;
  width?: number;
  point_x: number;
  bim_point_x: string;
  bim_point_y: string;
  bim_point_z: string;
  point_y: number;
  report_floor_id: string;
  report_id: string;
  priority: number;
  capture_time_stamp?: number;
  lat?: number | string;
  lng?: number | string;
  matching_points?: MatchingPoint[];
  heading_angle: number;
  vertical_angle: number;
  horizontal_angle: number;
  bim_offset: number;
  default_bim_offset: number;
  is_selected_point: number | null;
  map_point_radius: number | null;

  get floor(): Floor {
    return this._project.getFloor(this.floor_id);
  }

  get report(): any {
    return this._project.getReportById(this.report_id);
  }

  get project(): ProjectInfo {
    return this._project.projectInfo;
  }

  get observations(): ObservationRefactor[] {
    return this._project.getObservationsByPImgs(this.id);
  }

  get emptyPoint(): boolean {
    return this.image_360 === 'assets/krpano/media/space.png';
  }

  constructor(projectContext: Project360Service, pImgId?: 'auto' | string, priority = 0) {
    this._project = projectContext;

    if (pImgId && pImgId !== 'auto') {
      this.initPImg(pImgId, priority);
    } else if (pImgId && pImgId === 'auto') {
      this.initPImgByReportId();
    }
  }

  initPImg(pImgId: string, priority = 0): void {
    const pImg = this._project.getPImg(pImgId);
    if (pImg) {
      this.floor_id = pImg.floor_id;
      this.id = pImg.id;
      this.image_360 = pImg.image_360;
      this.image_360_thumb = pImg.image_360_thumb;
      this.navigation_hotspots = pImg.navigation_hotspots;
      this.numbers_of_completed_observation = pImg.numbers_of_completed_observation;
      this.numbers_of_in_progress_observation = pImg.numbers_of_in_progress_observation;
      this.numbers_of_pending_observation = pImg.numbers_of_pending_observation;
      this.blurred_faces = pImg.blurred_faces;
      this.height = pImg.height;
      this.width = pImg.width;
      this.point_id = pImg.point_id;
      this.point_name = pImg.point_name;
      this.point_x = pImg.point_x;
      this.point_y = pImg.point_y;
      this.bim_point_x = pImg.bim_point_x;
      this.bim_point_y = pImg.bim_point_y;
      this.bim_point_z = pImg.bim_point_z;
      this.bim_offset = pImg.bim_offset;
      this.default_bim_offset = pImg.default_bim_offset;
      this.report_floor_id = pImg.report_floor_id;
      this.report_id = pImg.report_id;
      this.priority = priority;
      this.capture_time_stamp = pImg.capture_time_stamp;
      this.lat = pImg.lat;
      this.lng = pImg.lng;
      this.matching_points = pImg.matching_points;
      this.heading_angle = pImg.heading_angle;
      this.horizontal_angle = pImg.horizontal_angle;
      this.vertical_angle = pImg.vertical_angle;
      this.is_selected_point = pImg.is_selected_point;
      this.map_point_radius = pImg.map_point_radius;
    }
  }

  initEmptyPoint(
    point: ProjectFloorPoint | null | undefined,
    floorId: string,
    reportId: string,
  ): void {
    const safeReportId = (reportId || 'no-report').toString();
    const safeFloorId = (floorId || 'no-floor').toString();
    const safePointId = (point?.id || 'no-point').toString();

    this.floor_id = safeFloorId;
    this.id =
      'noImg_' +
      safeReportId.slice(0, 8) +
      '-' +
      safeFloorId.slice(0, 8) +
      '-' +
      safePointId.slice(0, 8);
    this.image_360 = 'assets/krpano/media/space.png';
    this.image_360_thumb = 'assets/krpano/media/space.png';
    this.navigation_hotspots = [];
    this.numbers_of_completed_observation = 0;
    this.numbers_of_in_progress_observation = 0;
    this.numbers_of_pending_observation = 0;
    this.point_id = point?.id || '';
    this.point_name = point?.point_name || '';
    this.point_x = point?.point_x ?? 0;
    this.point_y = point?.point_y ?? 0;
    this.is_selected_point = point?.is_selected_point ?? null;
    this.map_point_radius = point?.map_point_radius ?? null;
    this.report_floor_id = safeFloorId;
    this.report_id = safeReportId;
    GStatic.log('EmptyPImg is Prepared', this.id);
  }

  initPImgByReportId(priority = 0): void {
    const lastReportId = this._project.projectInfo.lastReportId;
    const pImg = this._project.filterPImgsBy('report', lastReportId)[0];

    this.initPImg(pImg?.id, priority);
  }

  initPImgByFloor(floorId: string): void {
    const pImgs = this._project.filterPImgsBy('floor', floorId);
    this.initPImg(pImgs[0].id);
  }

  initPImgByReport(reportId: string): void {
    const pImgs = this._project.filterPImgsBy('report', reportId);
    this.initPImg(pImgs[0].id);
  }
}
